'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Checkbox } from '@repo/ui/components';
import { cn } from '@repo/ui/lib/utils';
import { useFieldSelection } from './context/column-settings.ts';
import { useNewFieldIds, useTableState } from './context/table-state-provider.tsx';
import type { ColumnField, DisplayChoice } from './tanstack-table';

/** A Display choice and the option it shows. */
export type ChosenDisplayChoice = {
  choice: DisplayChoice;
  value: string;
};

/** A field's Display choice, under its field: the choice's label, then each option as a switch. */
const FieldDisplayChoice = t0 => {
  const $ = _c(17);
  const {
    choice: t1,
    columnId
  } = t0;
  const {
    choice,
    value
  } = t1;
  const {
    setDisplayChoice
  } = useTableState();
  const t2 = choice.label;
  let t3;
  if ($[0] !== choice.label) {
    t3 = <span className="text-muted-foreground">{choice.label}</span>;
    $[0] = choice.label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== choice.id || $[3] !== choice.options || $[4] !== columnId || $[5] !== setDisplayChoice || $[6] !== value) {
    let t5;
    if ($[8] !== choice.id || $[9] !== columnId || $[10] !== setDisplayChoice || $[11] !== value) {
      t5 = (option, index) => <span className="flex items-center gap-1" key={option.value}>{index > 0 && <span aria-hidden={true} className="text-muted-foreground">·</span>}<button aria-pressed={option.value === value} className="rounded-sm px-1 py-0.5 text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/50 aria-pressed:font-semibold aria-pressed:text-foreground aria-pressed:no-underline" onClick={() => setDisplayChoice(columnId, choice.id, option.value)} type="button">{option.label}</button></span>;
      $[8] = choice.id;
      $[9] = columnId;
      $[10] = setDisplayChoice;
      $[11] = value;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = choice.options.map(t5);
    $[2] = choice.id;
    $[3] = choice.options;
    $[4] = columnId;
    $[5] = setDisplayChoice;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[13] !== choice.label || $[14] !== t3 || $[15] !== t4) {
    t5 = <div aria-label={t2} className="flex items-center gap-1 pl-6 text-xs" role="group" data-sentry-component="FieldDisplayChoice" data-sentry-source-file="settings-column-fields.tsx">{t3}{t4}</div>;
    $[13] = choice.label;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
type ColumnFieldChoices = {
  columnId: string;
  fields: readonly ColumnField[];
  newIds: ReadonlySet<string>;
  /** The ids of the fields shown, in code order. */
  shownIds: string[];
};
const NO_IDS: ReadonlySet<string> = new Set();

/** A column's Column fields, which are shown and which are New; none for a column without them. */
export const useColumnFieldChoices = columnId => {
  const $ = _c(10);
  const {
    fields,
    isShown
  } = useFieldSelection(columnId);
  const newIds = useNewFieldIds()[columnId] ?? NO_IDS;
  if (fields.length < 2) {
    return;
  }
  let t0;
  if ($[0] !== fields || $[1] !== isShown) {
    let t1;
    if ($[3] !== isShown) {
      t1 = field => isShown(field.id);
      $[3] = isShown;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = fields.filter(t1).map(_temp);
    $[0] = fields;
    $[1] = isShown;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[5] !== columnId || $[6] !== fields || $[7] !== newIds || $[8] !== t0) {
    t1 = {
      columnId,
      fields,
      newIds,
      shownIds: t0
    };
    $[5] = columnId;
    $[6] = fields;
    $[7] = newIds;
    $[8] = t0;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};

/**
 * A checkbox for each Column field, in code order, with the Display choice of a shown field under
 * it. A fixed field, or the last one shown, cannot be unticked.
 */
export const ColumnFieldsPicker = t0 => {
  const $ = _c(17);
  const {
    columnId,
    fieldChoices: t1,
    fields,
    newIds,
    shownIds
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const fieldChoices = t2;
  const {
    setFieldSelection
  } = useTableState();
  let t3;
  if ($[2] !== columnId || $[3] !== fieldChoices || $[4] !== fields || $[5] !== newIds || $[6] !== setFieldSelection || $[7] !== shownIds) {
    let t4;
    if ($[9] !== columnId || $[10] !== fieldChoices || $[11] !== newIds || $[12] !== setFieldSelection || $[13] !== shownIds) {
      t4 = field => {
        const shown = shownIds.includes(field.id);
        const isLocked = field.fixed || shown && shownIds.length === 1;
        const checkboxId = `table-settings-field-${columnId}-${field.id}`;
        const fieldChoice = fieldChoices.find(t5 => {
          const {
            choice
          } = t5;
          return choice.field === field.id;
        });
        return <li className="flex flex-col gap-0.5 px-1 py-1" key={field.id}><span className="flex min-w-0 items-center gap-2"><Checkbox aria-describedby={isLocked ? `${checkboxId}-hint` : undefined} aria-label={field.label} checked={shown} data-column-field={true} disabled={isLocked} id={checkboxId} onCheckedChange={value => setFieldSelection(columnId, value ? [...shownIds, field.id] : shownIds.filter(id => id !== field.id))} /><label className={cn("min-w-0 flex-1 truncate text-sm", isLocked && "text-muted-foreground")} htmlFor={checkboxId}>{field.label}</label>{newIds.has(field.id) && <Badge variant="secondary">New</Badge>}{isLocked && <span className="text-xs text-muted-foreground" id={`${checkboxId}-hint`}>{field.fixed ? "Always shown" : "Hide the column instead"}</span>}</span>{fieldChoice && <FieldDisplayChoice choice={fieldChoice} columnId={columnId} />}</li>;
      };
      $[9] = columnId;
      $[10] = fieldChoices;
      $[11] = newIds;
      $[12] = setFieldSelection;
      $[13] = shownIds;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = fields.map(t4);
    $[2] = columnId;
    $[3] = fieldChoices;
    $[4] = fields;
    $[5] = newIds;
    $[6] = setFieldSelection;
    $[7] = shownIds;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[15] !== t3) {
    t4 = <ul className="flex flex-col gap-0.5" data-sentry-component="ColumnFieldsPicker" data-sentry-source-file="settings-column-fields.tsx">{t3}</ul>;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
function _temp(field_0) {
  return field_0.id;
}